import { of, from, merge, EMPTY } from "rxjs";
import {
  switchMap,
  timeout,
  catchError,
  switchMapTo,
  throttleTime,
  filter,
} from "rxjs/operators";
import { ofHubEventPrefix } from "@services/hub";

export const GUARDS_INIT = "pss/guards/INIT";
export const GUARDS_REQUESTED = "pss/guards/REQUESTED";
export const GUARDS_GRANTED = "pss/guards/GRANTED";
export const GUARDS_REJECTED = "pss/guards/REJECTED";
export const GUARDS_REVOKED = "pss/guards/REVOKED";
export const GUARDS_ELAPSED = "pss/guards/ELAPSED";
export const GUARDS_PENDING = "pss/guards/PENDING";
export const GUARDS_READ = "pss/guards/READ";
export const GUARDS_DEHIGHLIGHT_ALL = "pss/guards/DEHIGHLIGHT_ALL";

const initialState = {
  list: [],
  unreadCount: 0
};

function updateStateList(list, requestId, changes) {
  if (!requestId || !changes) return list;

  const found = list.find(x => x.requestId === requestId);
  if (found) {
    return [
      ...list.filter(x => x.requestId !== requestId),
      {
        ...found,
        ...changes,
        actor: { ...found.actor, ...changes.actor }
      }
    ]
  }
  return [
    ...list,
    { ...changes }
  ];
}

function updateState(state, requestId, changes) {
  const newList = updateStateList(state.list, requestId, changes);
  return {
    ...state,
    list: newList,
    unreadCount: newList.reduce((acc, n) => (n.unread ? acc + 1 : acc), 0)
  };
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case GUARDS_INIT:
      return updateState({ list: action.payload.map(g => g.highlighted = g.isSticky) });
    case GUARDS_REQUESTED:
      return updateState(state, action.requestId, {
        ...action,
        unread: true,
        highlighted: action.isOwn || false,
        isPending: false,
        isRequested: true
      });
    case GUARDS_GRANTED:
      return updateState(state, action.requestId, {
        ...action,
        unread: false,
        highlighted: action.isOwn || false,
        isPending: false,
        isRequested: false,
        isGranted: true
      });
    case GUARDS_REJECTED:
      return updateState(state, action.requestId, {
        ...action,
        unread: false,
        highlighted: false,
        isPending: false,
        isRequested: false,
        isGranted: false,
        isRejected: true
      });
    case GUARDS_REVOKED:
      return updateState(state, action.requestId, {
        ...action,
        unread: false,
        highlighted: false,
        isPending: false,
        isRequested: false,
        isGranted: false,
        isRejected: false,
        isRevoked: true
      });
    case GUARDS_ELAPSED:
      return updateState(state, action.requestId, {
        ...action,
        isPending: false,
        isRequested: false,
        isGranted: false,
        isRejected: false,
        isElapsed: true
      });
    case GUARDS_PENDING:
      return updateState(state, action.requestId, {
        ...action,
        isPending: true
      });
    case GUARDS_READ:
      return {
        ...state,
        list: state.list.map((n) => ({ ...n, unread: false })),
        unreadCount: 0,
      };
    case GUARDS_DEHIGHLIGHT_ALL:
      return {
        ...state,
        list: state.list.map((n) => ({ ...n, highlighted: false })),
      };
    default:
      return state;
  }
}

export function guardsInit(authorizations = []) {
  return {
    type: GUARDS_INIT,
    payload: authorizations.map((n) => ({
      ...n,
      unread: false,
      highlighted: false,
    })),
  };
}

export function guardsRead(notifications) {
  return { type: GUARDS_READ, payload: notifications };
}

export function guardsDehighlightAll() {
  return { type: GUARDS_DEHIGHLIGHT_ALL };
}

export function guardsPending(requestId) {
  return { type: GUARDS_PENDING, requestId };
}

function isOwnAuth(data, activeUserId, isAdministrator) {
  if (!data?.actor) return undefined;
  return data.actor.id === activeUserId;
}

export function notifyGuards(action$, store$) {
  return action$.pipe(
    ofHubEventPrefix("Guard."),
    switchMap(({ payload }) => {
      const { isAdministrator, activeUserId } = store$.value.session;
      const isOwn = isOwnAuth(payload.data, activeUserId, isAdministrator);
      switch (payload.id) {
        case "Guard.AuthorizationRequested":
          return of({ type: GUARDS_REQUESTED, ...payload.data, isOwn });

        case "Guard.AuthorizationGranted":
          return of({ type: GUARDS_GRANTED, ...payload.data, isOwn });

        case "Guard.AuthorizationRejected":
          return of({ type: GUARDS_REJECTED, ...payload.data, isOwn });

        case "Guard.AuthorizationRevoked":
          return of({ type: GUARDS_REVOKED, ...payload.data, isOwn });

        case "Guard.AuthorizationElapsed":
          return of({ type: GUARDS_ELAPSED, ...payload.data, isOwn });

        default:
          return EMPTY;
      }
    })
  );
}

export const guardEpics = [notifyGuards];